import { ReactNode, useMemo, useReducer, useRef } from "react";

import { actions, ActionTypes, DispatchContext, DispatchWindows, reducer, State, StateContext } from "./reducer";

type OpenLikeAction = ReturnType<typeof actions.openWindow> | ReturnType<typeof actions.toggleWindow>;

function isOpenLikeAction(action: unknown): action is OpenLikeAction {
    if (typeof action !== "object" || action === null) {
        return false;
    }
    const type = (action as { type: unknown }).type;
    return type === ActionTypes.OpenWindow || type === ActionTypes.ToggleWindow;
}

export function WindowsProvider({
    children,
    initialState: customInitialState,
    autoDockOnOpen = false,
}: {
    children: ReactNode;
    initialState?: State;
    /**
     * Forces `dock: true` on every `openWindow`/`toggleWindow` that didn't set
     * the flag explicitly. Set on desktop flavors so panels land in the dock
     * atomically; leave `false` on mobile (side panels/drawers).
     */
    autoDockOnOpen?: boolean;
}) {
    const effectiveInitialState = customInitialState ?? {};
    const [state, dispatch] = useReducer(reducer, effectiveInitialState);
    const lazyValue = useRef(state);
    lazyValue.current = state;

    const enhancedDispatch = useMemo<DispatchWindows>(() => {
        if (!autoDockOnOpen) {
            return dispatch;
        }
        return (action) => {
            if (isOpenLikeAction(action) && action.dock == null) {
                dispatch({ ...action, dock: true });
                return;
            }
            dispatch(action);
        };
    }, [autoDockOnOpen]);

    return (
        <StateContext.Provider value={state}>
            <DispatchContext.Provider value={enhancedDispatch}>{children}</DispatchContext.Provider>
        </StateContext.Provider>
    );
}
